/**
 * Created by neo on 02.12.20.
 *
 * Simplified model matching backend WeekDayEntry.
 * Backend pre-resolves all display properties (title, subtitle, media, showAsDone, etc.)
 *
 * Plain TypeScript type - no MobX observables.
 */
import { v4 as UUID } from 'uuid';
import dayjs, { Dayjs } from 'dayjs';
import { Media, createMedia } from '../../Media/Media';
import { WeekDayEntryType } from './WeekDayEntryType';

export type NavigationAction = {
  type: string;
  payload?: Record<string, any>;
  source?: string;
  target?: string;
};

export type LinkProps = {
  screen: string;
  params?: Record<string, any>;
  href?: string;
  action?: NavigationAction;
};

export type WeekDayEntryBase = {
  id: string;
  type: WeekDayEntryType;
  startTime: string;
  endTime?: string | null;
  isUtc: boolean;
  title: string;
  subtitle: string;
  description: string;
  media?: Media;
  showAsDone: boolean;
  disabled: boolean;
  typeName?: string;
  durationText?: string;
  timeText?: string;
  contextInfo?: string;
  isVideo: boolean;
  linkProps?: LinkProps;
};

export type WeekDayEntry = WeekDayEntryBase;

export type { WeekDayEntryWorkout } from './WeekDayEntryWorkout';
export type { WeekDayEntryEvent } from './WeekDayEntryEvent';
export type { WeekDayEntryActivityLog } from './WeekDayEntryActivityLog';
export type { WeekDayEntrySubscribedEvent } from './WeekDayEntrySubcribedEvent';
export type { WeekDayEntryMindfulnessUnit } from './WeekDayEntryMindfulnessUnit';

import type { WeekDayEntryWorkout } from './WeekDayEntryWorkout';
import type { WeekDayEntryEvent } from './WeekDayEntryEvent';
import type { WeekDayEntryActivityLog } from './WeekDayEntryActivityLog';
import type { WeekDayEntrySubscribedEvent } from './WeekDayEntrySubcribedEvent';
import type { WeekDayEntryMindfulnessUnit } from './WeekDayEntryMindfulnessUnit';

export type WeekDayEntryUnion =
  | WeekDayEntryWorkout
  | WeekDayEntryEvent
  | WeekDayEntryActivityLog
  | WeekDayEntrySubscribedEvent
  | WeekDayEntryMindfulnessUnit;

export function isWeekDayEntryWorkout(entry: WeekDayEntryBase): entry is WeekDayEntryWorkout {
  return entry.type === 'workout';
}

export function isWeekDayEntryEvent(entry: WeekDayEntryBase): entry is WeekDayEntryEvent {
  return entry.type === 'event';
}

export function isWeekDayEntryActivityLog(entry: WeekDayEntryBase): entry is WeekDayEntryActivityLog {
  return entry.type === 'activityLog';
}

export function isWeekDayEntrySubscribedEvent(entry: WeekDayEntryBase): entry is WeekDayEntrySubscribedEvent {
  return entry.type === 'subscribedEvent';
}

export function isWeekDayEntryMindfulnessUnit(entry: WeekDayEntryBase): entry is WeekDayEntryMindfulnessUnit {
  return entry.type === 'mindfulnessUnit';
}

// --- Utility functions (replacing computed getters) ---

export function getEntryStartDayjs(e: WeekDayEntryBase): Dayjs {
  return e.isUtc ? dayjs(dayjs.utc(e.startTime, 'HH:mm:ss').toDate()) : dayjs.utc(e.startTime, 'HH:mm:ss');
}

export function getEntryScreen(e: WeekDayEntryBase): string | undefined {
  return e.linkProps?.screen;
}

export function getEntryParams(e: WeekDayEntryBase): Record<string, any> | undefined {
  return e.linkProps?.params;
}

export function getEntryLink(e: WeekDayEntryBase): string | undefined {
  return e.linkProps?.href;
}

export function entriesEqual(a: WeekDayEntryBase, b: WeekDayEntryBase): boolean {
  return a.id === b.id;
}

// --- Localization helper ---

/**
 * Resolves a value that may be a plain string, a LocalizedValue object {lang, value},
 * or an array of LocalizedValue objects [{lang, value}] into a plain string.
 *
 * The backend schedule API may return these fields as LocalizedValue arrays
 * even though the frontend model expects plain strings.
 */
const localizedLangPriority = ['en', 'de', 'fr', 'it'];

export function resolveLocalized(val: unknown, fallback = ''): string {
  if (typeof val === 'string') return val;
  if (!val) return fallback;

  // Array of {lang, value} -> pick best language
  if (Array.isArray(val)) {
    if (val.length === 0) return fallback;
    for (const lang of localizedLangPriority) {
      const match = val.find((v: any) => v?.lang === lang && v?.value);
      if (match) return match.value;
    }
    // Fallback to first entry with a value
    const first = val.find((v: any) => v?.value);
    return first?.value ?? fallback;
  }

  // Single {lang, value} object
  if (typeof val === 'object' && 'value' in (val as any)) {
    return (val as any).value ?? fallback;
  }

  return String(val);
}

// --- Time helper ---

function fixTime(time?: string | null): string | undefined {
  if (time) {
    if (time.length === 5) {
      return `${time}:00`;
    } else if (time.length >= 8) {
      return time.substring(0, 8);
    }
    return time;
  }
  return undefined;
}

// --- Parse function (replaces MobX factory) ---

export function parseWeekDayEntry(json?: any): WeekDayEntryBase {
  const startTime = fixTime(json?.startTime) || '18:00:00';
  return {
    id: json?.id ?? UUID(),
    type: json?.type ?? 'workout',
    startTime,
    endTime: fixTime(json?.endTime) ?? startTime,
    isUtc: json?.isUtc ?? false,
    title: resolveLocalized(json?.title),
    subtitle: resolveLocalized(json?.subtitle),
    description: resolveLocalized(json?.description),
    media: json?.media ? createMedia(json.media) : undefined,
    showAsDone: json?.showAsDone ?? false,
    disabled: json?.disabled ?? false,
    typeName: resolveLocalized(json?.typeName) || undefined,
    durationText: resolveLocalized(json?.durationText) || undefined,
    timeText: resolveLocalized(json?.timeText) || undefined,
    contextInfo: resolveLocalized(json?.contextInfo) || undefined,
    isVideo: json?.isVideo ?? false,
    linkProps: json?.linkProps,
  };
}

/** @deprecated Use parseWeekDayEntry instead */
export const createWeekDayEntry = parseWeekDayEntry;
